import type {
  BlockComponent,
  BlockResolver,
  PrimitiveComponent,
  PrimitiveKind,
  ResolverCatalog,
  SystemComponent,
  SystemKind,
} from './types';

/**
 * Build a {@link BlockResolver} from a host-supplied catalog. The
 * returned resolver is what hosts pass to {@code BlockResolverProvider}
 * at the root of their app.
 *
 * <p>Resolution order for blocks:
 * <ol>
 *   <li>{@code themes[themeId].blocks[blockType]} — themed override</li>
 *   <li>{@code themes.default.blocks[blockType]} — default-theme override</li>
 *   <li>{@code defaultBlocks[blockType]} — the canonical implementation</li>
 *   <li>{@code undefined} — caller renders an "unknown block" placeholder</li>
 * </ol>
 *
 * <p>Same order for system components, minus the default-blocks fall
 * through (system pages don't have a canonical implementation outside
 * themes; the caller falls back to its hardcoded React when no theme
 * overrides the kind).
 *
 * <p>Port of ThemeMaker's {@code resolveBlock} / {@code resolveSystem}
 * in {@code components/block-system/themeRegistry.tsx} — factory shape
 * instead of module-level singleton so different hosts in one workspace
 * can carry different catalogs.
 */
export function createBlockResolver(catalog: ResolverCatalog): BlockResolver {
  const { defaultBlocks, themes = {} } = catalog;

  function resolveBlock(
    themeId: string | null | undefined,
    blockType: string,
  ): BlockComponent | undefined {
    const id = themeId ?? 'default';
    const themed = themes[id]?.blocks?.[blockType];
    if (themed) return themed;
    if (id !== 'default') {
      const defaultThemed = themes.default?.blocks?.[blockType];
      if (defaultThemed) return defaultThemed;
    }
    return defaultBlocks[blockType];
  }

  function resolveSystem(
    themeId: string | null | undefined,
    kind: SystemKind,
  ): SystemComponent | undefined {
    const id = themeId ?? 'default';
    return themes[id]?.system?.[kind] ?? themes.default?.system?.[kind];
  }

  function resolvePrimitive(
    themeId: string | null | undefined,
    kind: PrimitiveKind,
  ): PrimitiveComponent | undefined {
    const id = themeId ?? 'default';
    return themes[id]?.primitives?.[kind] ?? themes.default?.primitives?.[kind];
  }

  return { resolveBlock, resolveSystem, resolvePrimitive };
}
